import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, MapPin, CheckCircle2, Loader2, Lock, Sprout, Star, Award, Trophy, Store } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useT } from "../lib/i18n";
import { DISTRICTS, CATEGORIES, HOURS, EXPERIENCE } from "../lib/options";
import { ChoiceCard, HourCard, Progress, Spinner, Empty } from "../components/Bits";

const EXP_ICONS = { none: Sprout, lt1: Star, "1to3": Award, gt3: Trophy };
const TOTAL = 5;
const DRAFT = "wis_job_draft";

export default function PostJob() {
  const { t } = useT();
  const nav = useNavigate();
  const { id: editId } = useParams();
  const [s, setS] = useState(null);
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [j, setJ] = useState(() => (!editId && JSON.parse(localStorage.getItem(DRAFT) || "null")) || { title: "", category: "", location: "", branch: "", hours: [], experience: "none", salary: "", description: "" });
  const [orgLocs, setOrgLocs] = useState([]);
  const [loaded, setLoaded] = useState(!editId);

  useEffect(() => {
    let live = true;
    api.get("/employer/summary").then((r) => { if (!live) return; setS(r.data); if (!editId && r.data.org?.district) setJ((x) => ({ ...x, location: x.location || r.data.org.district })); }).catch(() => live && setS({}));
    api.get("/employer/org").then((r) => live && setOrgLocs(r.data?.locations || [])).catch(() => {});
    if (editId) api.get(`/employer/jobs/${editId}`).then((r) => { if (!live) return; setJ({ title: r.data.title, category: r.data.category, location: r.data.location, branch: r.data.branch || "", hours: r.data.hours || [], experience: r.data.experience || "none", salary: r.data.salary || "", description: r.data.description || "" }); setLoaded(true); }).catch((e) => toast.error(errMsg(e)));
    return () => { live = false; };
  }, [editId]);
  useEffect(() => { if (!editId) localStorage.setItem(DRAFT, JSON.stringify(j)); }, [j, editId]);

  if (!s || !loaded) return <Spinner />;
  if (!s.org) return <Empty title={t("setup_t")}><Link to="/employer/setup" className="btn-orange" data-testid="postjob-setup-btn">{t("setup_t")}</Link></Empty>;
  if (!s.subscription && !editId) return (
    <div className="narrow py-10"><Empty icon={Lock} title={t("pj_locked")} sub={s.pending_payment ? t("payment_pending") : t("no_plan_sub")} testid="postjob-locked">
      {!s.pending_payment && <Link to="/employer/subscription" className="btn-orange" data-testid="postjob-choose-plan-btn">{t("choose_plan")}</Link>}
    </Empty></div>
  );
  if (done) return (
    <div className="narrow flex flex-col items-center py-14 text-center fade-up" data-testid="postjob-done">
      <CheckCircle2 size={80} className="text-emerald-500" />
      <h1 className="mt-4 text-3xl font-bold text-[color:var(--navy)]">{t(editId ? "job_saved_t" : "pj_ok_t")}</h1>
      {!editId && <p className="mt-2 text-slate-600">{t("pj_ok_sub")}</p>}
      <button className="btn-orange mt-8 !w-full" onClick={() => nav(editId ? "/employer/jobs" : "/employer")} data-testid="postjob-done-btn">{t("ok")}</button>
    </div>
  );

  const set = (k, v) => setJ((x) => ({ ...x, [k]: v }));
  const valid = { 1: j.title.trim().length > 2 && j.category, 2: !!j.location, 3: j.hours.length > 0, 4: !!j.experience, 5: true }[step];
  const titles = { 1: "pj_title_t", 2: "pj_loc_t", 3: "pj_hours_t", 4: "pj_exp_t", 5: "pj_details_t" };
  const publish = async () => {
    setBusy(true);
    try {
      const body = { ...j, branch: j.branch || null };
      if (editId) await api.put(`/employer/jobs/${editId}`, body); else { await api.post("/employer/jobs", body); localStorage.removeItem(DRAFT); }
      setDone(true);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="narrow pb-40 pt-6" data-testid={`postjob-step-${step}`}>
      <Progress step={step} total={TOTAL} />
      {editId && <p className="mt-4 font-bold text-[color:var(--orange-dark)]" data-testid="postjob-edit-mode">{t("edit_job_t")}</p>}
      <h1 className="mt-6 text-3xl font-bold text-[color:var(--navy)]">{t(titles[step])}</h1>
      <div className="mt-6 space-y-3">
        {step === 1 && (
          <>
            <label className="label" htmlFor="pj-title">{t("pj_title")}</label>
            <input id="pj-title" className="field" placeholder={t("pj_title_ph")} value={j.title} onChange={(e) => set("title", e.target.value)} data-testid="postjob-title-input" />
            <p className="label pt-3">{t("pj_cat")}</p>
            {CATEGORIES.map(({ v, icon }) => <ChoiceCard key={v} icon={icon} label={t(`cat_${v}`)} selected={j.category === v} onClick={() => set("category", v)} testid={`postjob-cat-${v}`} />)}
          </>
        )}
        {step === 2 && (
          <>
            {orgLocs.map((l, i) => <ChoiceCard key={`b${i}`} icon={Store} label={l.name} sub={`${l.district}${l.address ? ` · ${l.address}` : ""}`} selected={j.branch === l.name && j.location === l.district} onClick={() => setJ((x) => ({ ...x, branch: l.name, location: l.district }))} testid={`postjob-branch-${i}`} />)}
            {orgLocs.length > 0 && <p className="label pt-3">{t("other_place")}</p>}
            {DISTRICTS.map((d) => <ChoiceCard key={d} icon={MapPin} label={d} selected={!j.branch && j.location === d} onClick={() => setJ((x) => ({ ...x, branch: "", location: d }))} testid={`postjob-loc-${d.replace(/\s/g, "-").toLowerCase()}`} />)}
          </>
        )}
        {step === 3 && HOURS.map(({ v, icon }) => <HourCard key={v} icon={icon} label={t(`h_${v}`)} help={t(`h_${v}_d`)} selected={j.hours.includes(v)} onClick={() => set("hours", j.hours.includes(v) ? j.hours.filter((x) => x !== v) : [...j.hours, v])} testid={`postjob-hours-${v}`} />)}
        {step === 4 && EXPERIENCE.map((v) => <ChoiceCard key={v} icon={EXP_ICONS[v]} label={v === "none" ? t("e_none_job") : t(`e_${v}`)} selected={j.experience === v} onClick={() => set("experience", v)} testid={`postjob-exp-${v}`} />)}
        {step === 5 && (
          <>
            <label className="label" htmlFor="pj-salary">{t("pj_salary")}</label>
            <input id="pj-salary" className="field" placeholder={t("pj_salary_ph")} value={j.salary} onChange={(e) => set("salary", e.target.value)} data-testid="postjob-salary-input" />
            <label className="label pt-3" htmlFor="pj-desc">{t("pj_desc")}</label>
            <textarea id="pj-desc" rows={4} className="field !py-3" placeholder={t("pj_desc_ph")} value={j.description} onChange={(e) => set("description", e.target.value)} data-testid="postjob-desc-input" />
            <div className="card-soft mt-4 space-y-1 p-4" data-testid="postjob-review">
              <p className="text-sm font-semibold text-slate-500">{t("pj_review_t")}</p>
              <p className="text-lg font-bold">{j.title}</p>
              <p className="text-slate-700">{t(`cat_${j.category}`)} · {j.branch ? `${j.branch}, ` : ""}{j.location}</p>
              <p className="text-slate-700">{j.hours.map((h) => t(`h_${h}`)).join(", ")}</p>
            </div>
          </>
        )}
      </div>
      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white/95 p-4 pb-safe backdrop-blur">
        <div className="mx-auto flex max-w-xl gap-3">
          {step > 1 && <button className="btn-ghost !w-auto !px-5" onClick={() => setStep(step - 1)} data-testid="postjob-prev-btn"><ArrowLeft size={22} /></button>}
          {step < TOTAL ? (
            <button className="btn-orange flex-1" disabled={!valid} onClick={() => { setStep(step + 1); window.scrollTo(0, 0); }} data-testid="postjob-next-btn">{t("next")}<ArrowRight size={22} /></button>
          ) : (
            <button className="btn-orange flex-1" disabled={busy} onClick={publish} data-testid="postjob-publish-btn">{busy && <Loader2 className="animate-spin" />}{t(editId ? "save_changes" : "publish")}</button>
          )}
        </div>
      </div>
    </div>
  );
}
